import { mapActions, mapGetters, mapState } from 'vuex';
import Draggable from 'vuedraggable';

import {
    getGoogleSpreadSheetUrl,
    isBlendableSource,
    hasValidBlendSetting,
    DISPLAY_CHANNEL_NAME_CHANNELS,
    checkIsAnalyticSource,
    refineChannel,
    blendingActivated,
    mappingActivated,
    isMappableSource,
} from '@/utils/dashboard';
import { isValueExist, changeOrder } from '@/utils/utils';
import { mixpanelLogger } from '@/utils/tracker/mixpanel';

import { DIRECTIVE_CLAMP as _DIRECTIVE_CLAMP } from '@/styles/common.styled';

import SearchInput from '@components/Common/searchInput/SearchInput';
import PermissionTooltip from '@components/Common/PermissionTooltip';
import ConnectionPanel from '@components/Dashboard/DashboardConnection';
import SecondPanel from '../common/secondPanel';

import PanelOptionMixin from '../mixin';
import dragMixin from '@/mixins/drag';
import channelIconMixin from '@mixin/channelIcons';

import DataSourceIcon from '@svgIcons/dashboard/data_source.svg';

import MoveIcon from '@svgIcons/move.svg';
import CloseIcon from '@svgIcons/close.svg';
import EditIcon from '@svgIcons/edit.svg';
import PlusIcon from '@svgIcons/plus.svg';
import GssLinkIcon from '@svgIcons/gss_link.svg';
import WarningIcon from '@svgIcons/warning3.svg';
import NextIcon from '@svgIcons/next.svg';

import * as C from '../styles';
import {
    SouceItemWrapper,
    Logo,
    AskingModalTitle,
    AskingModalDesc,
    AskingModalOptionWrapper,
    AskingModalOption,
    AskingModalWrapper,
    BlendedTag,
    BlendedTagWrapper,
    BlendedText,
    MappingTag,
    DataSourceItemBox,
    SOURCE_STATUS,
    DisconnectedInfoWrapper,
    InvalidInfoWrapper,
    AdditionalInfoText,
    AdditionalInfoCta,
    AdditionalInfoCtaSlot,
    DisconnectedInfoCtaText,
    InvalidInfoCtaText,
} from './styles';

import Dialog from '@/components/Common/Dialog';
import { IFRAME_EVENT_MESSAGE, WIDGET_TYPES } from '@/utils/consts';
import ClickToCopyAssetId from '../../../ClickToCopyAssetId.vue';
import withHMR from '@/utils/withHMR';
import { getChannelName } from '@/utils/channelName';
import {
    resolveTeamspaceAllowedChannelIds,
    resolveTeamspaceAllowedAssetIds,
} from '@/utils/filter';
import { getAccountKey } from '@/components/Dashboard/common/WidgetSettingPanel';

import { refineRawText } from '@/utils/translator';
import { refineChannelLogoSrc } from '@/utils/account';
import {
    isMarketingLikeSource,
    isMarketingSource,
    requireDateSettingSource,
} from './utils';

const {
    /** widget */
    PIVOT_TABLE,
    PIVOT_TABLE_ANALYTIC,
    CREATIVE,
    GRAPH,
    OVERVIEW,
    SINGLE_METRIC,
    SMALL_TABLE,
    GOAL,

    /** page */
    DATA_STERAM,
    AUTOMATION,
} = WIDGET_TYPES;

export { requireDateSettingSource };

const ALL_CHECK_DISABLE_CATEGORIES = [
    'bigQuery',
    'googleSpreadSheet',
    'mailchimp',
    'all',
    'redshift',
];

export const UNSELECT_ALREADY_SELECTED = 'UNSELECT_ALREADY_SELECTED';

const DIRECTIVE_CLAMP = R.assocPath(
    ['directives', 0, 'value'],
    3,
    _DIRECTIVE_CLAMP
);

const UPDATE_KEY = 'settings';
const compareFn = R.compose(hasValidBlendSetting, R.propOr({}, UPDATE_KEY));

const MAX_TEMPLATE_AD_ACCOUNTS_PER_CHANNEL = 2;

const PanelDataSourceOption = {
    name: 'PanelDataSourceOption',
    mixins: [
        channelIconMixin,
        PanelOptionMixin,
        dragMixin({ cursorNormal: 'grab' }),
    ],
    props: {
        showAddConnection: {
            type: Boolean,
            default: true,
        },
        pivotTableMode: String,
        hide: {
            type: Boolean,
            default: false,
        },
        ignoreIsAnalytic: {
            type: Boolean,
            default: false,
        },
        disabledTooltipGenerator: {
            type: Function,
        },
        compatibilityLoading: {
            type: Boolean,
            default: false,
        },
        analyticGenerateOption: {
            type: Function,
            default: R.always({ options: [], message: '' }),
        },
        isTemplateView: Boolean,
    },
    data() {
        return {
            connectionPanel: false,
            askingModal: false,
            selectedData: {},
            redirectTargetItem: null,
        };
    },
    computed: {
        ...mapState('newDashboard', [
            'tableAdAccounts',
            'teamspaceAllowedChannelIds',
            'teamspaceAllowedAssetIds',
        ]),
        ...mapGetters('user', [
            'accountToken',
            'pricePlanSettings',
            'hasOnlyAvailableConnectors',
            'isLimitedUser',
            'isQa',
        ]),
        ...mapGetters('permission', ['gteEdit']),
        ...mapGetters('newDashboard', ['tableAdAccountsByAccountKey']),
        ...mapGetters('permission', [
            'connectionDataSourcesManageable',
            'connectionManageable',
        ]),
        adAccountsMapByChannel() {
            const { tableAdAccounts = [] } = this;

            return tableAdAccounts.reduce((acc, value) => {
                const flag = refineChannel(value);

                acc[flag] = [...(acc[flag] || []), value];

                return acc;
            }, {});
        },
        allowWorkspaceForBlendData: R.pathOr(true, [
            'pricePlanSettings',
            'allowDataSourceSettings',
        ]),
        availableList() {
            const { folderFilteredAssetIds, currentList } = this;
            return folderFilteredAssetIds !== null
                ? R.filter(
                      (item) =>
                          folderFilteredAssetIds.includes(Number(item.assetId)),
                      currentList
                  )
                : currentList;
        },
        visuallyHiddenList() {
            const { folderFilteredAssetIds, currentList } = this;
            return folderFilteredAssetIds !== null
                ? R.filter(
                      (item) =>
                          !folderFilteredAssetIds.includes(
                              Number(item.assetId)
                          ),
                      currentList
                  )
                : [];
        },
        folderFilteredAdAccountIds() {
            return resolveTeamspaceAllowedChannelIds(
                this.teamspaceAllowedChannelIds
            );
        },
        folderFilteredAssetIds() {
            const resolved = resolveTeamspaceAllowedAssetIds(
                this.teamspaceAllowedAssetIds
            );

            return resolved === null ? null : resolved.map(Number);
        },
        maxedOutChannelSet() {
            const grouped = R.groupBy(refineChannel, this.selectedList);
            const maxedOutChannels = Object.keys(grouped).filter(
                (channel) =>
                    grouped[channel].length >=
                    MAX_TEMPLATE_AD_ACCOUNTS_PER_CHANNEL
            );

            return new Set(maxedOutChannels);
        },
    },
    methods: {
        ...mapActions('newDashboard', ['getTableAdAccounts']),
        updateSource(dataSource) {
            const { indexKey } = this;

            const cList = [...this.currentList];
            const index = R.findIndex(
                R.propEq(indexKey, dataSource[indexKey]),
                cList
            );

            let updated = R.over(
                R.lensPath([index, 'selected']),
                (selected) => {
                    if (!selected) {
                        const options = this.generateOptions(dataSource);
                        if (isValueExist(options)) {
                            this.selectedData = dataSource;
                            this.askingModal = true;
                            return selected;
                        }
                    }

                    return !selected;
                },
                cList
            );

            const target = updated[index];
            updated = R.remove(index, 1, updated);
            updated = [...updated, target];

            this.$emit('update', updated);
        },
        renderList() {
            const {
                renderNoList,
                renderItem,
                disabled,
                availableList,
                visuallyHiddenList,
            } = this;

            const list = R.filter(R.prop('selected'), availableList);
            const model = [...availableList];

            return (
                <SouceItemWrapper>
                    {isValueExist(list) ? (
                        <Draggable
                            value={model}
                            animation={200}
                            group="row"
                            disabled={disabled}
                            ghostClass="ghost"
                            class="drag-area"
                            forceFallback={true}
                            on={{
                                start: () => (this.drag = true),
                                end: ({ oldIndex, newIndex }) => {
                                    const _f = R.findIndex(R.__, model);
                                    oldIndex = _f(R.equals(list[oldIndex]));
                                    newIndex = _f(R.equals(list[newIndex]));

                                    const newModel = changeOrder({
                                        oldIndex,
                                        newIndex,
                                        value: model,
                                    });

                                    const mergedModel = [
                                        ...newModel,
                                        ...visuallyHiddenList,
                                    ];

                                    this.$emit('update', mergedModel);
                                    this.drag = false;
                                },
                            }}
                        >
                            {list.map(renderItem)}
                        </Draggable>
                    ) : (
                        renderNoList(
                            this.$t('EDITABLE_DASHBOARD.list_empty_source')
                        )
                    )}
                </SouceItemWrapper>
            );
        },
        checkItemEditable(item) {
            if (this.widgetType === AUTOMATION) return false;

            return (
                this.allowWorkspaceForBlendData &&
                isBlendableSource(item) &&
                ((this.widgetType !== PIVOT_TABLE_ANALYTIC &&
                    this.widgetType !== DATA_STERAM) ||
                    requireDateSettingSource(item))
            );
        },
        renderEditIcon(item) {
            if (!this.checkItemEditable(item)) return null;

            return (
                <PermissionTooltip
                    visible={!this.connectionDataSourcesManageable}
                    stopClickPropagation
                >
                    <EditIcon
                        v-view-box
                        style={
                            !this.connectionDataSourcesManageable
                                ? { fill: 'var(--color-gray-40)' }
                                : undefined
                        }
                        onClick={(e) => {
                            e.stopPropagation();
                            this.redirectTargetItem = item;
                        }}
                    />
                </PermissionTooltip>
            );
        },
        renderSourceItemIcons(item) {
            return [this.renderEditIcon(item), this.renderGssLink(item)];
        },
        renderGssLink(item) {
            return [
                item.channel === 'googleSpreadSheet' &&
                    item.displayChannelName === 'Google Sheets' && (
                        <GssLinkIcon
                            class="share-icon"
                            v-view-box
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();

                                const url = getGoogleSpreadSheetUrl(item);

                                window.open(url, '_blank');
                            }}
                        />
                    ),
            ];
        },
        renderItem(savedAccount) {
            const {
                channelIconWithAdverity,
                cursor,
                updateSource,
                disabled,
                renderGssLink,
                tableAdAccountsByAccountKey,
                isQa,
            } = this;

            const latestAccount =
                tableAdAccountsByAccountKey[getAccountKey(savedAccount)];
            const account = latestAccount ?? savedAccount;

            const { name, isDeprecated } = account;

            const text = this.translator(account);

            const refinedLogo = refineChannelLogoSrc(account);
            const refinedChannel = refineChannel(account);

            const assetId = account?.assetId;

            const status = (() => {
                const isDisconnected = !isValueExist(latestAccount);

                if (isDisconnected) return SOURCE_STATUS.DISCONNECTED;
                if (isDeprecated) return SOURCE_STATUS.DEPRECATED;

                if (this.isAnalytic) {
                    const isInvalid = isMarketingSource(account);

                    if (isInvalid) return SOURCE_STATUS.INVALID;
                } else {
                    const isInvalid = !isMarketingLikeSource(account);

                    if (isInvalid) return SOURCE_STATUS.INVALID;
                }

                return SOURCE_STATUS.DEFAULT;
            })();

            return (
                <DataSourceItemBox
                    key={`${name}-${assetId}`}
                    dragCursor={cursor}
                    data-name={name}
                    status={status}
                >
                    <MoveIcon class="move" v-view-box />
                    {refinedLogo ? (
                        <Logo src={refinedLogo}></Logo>
                    ) : (
                        channelIconWithAdverity(refinedChannel)
                    )}
                    <div {...DIRECTIVE_CLAMP} class="item-text">
                        {text}
                    </div>
                    {mappingActivated(account) && (
                        <MappingTag isSelectedSource>
                            {this.$t(
                                'NEW_DASHBOARD.source_setting_option.tag.mapped'
                            )}
                        </MappingTag>
                    )}
                    {blendingActivated(account) && (
                        <BlendedTag isSelectedSource>
                            {this.$t(
                                'NEW_DASHBOARD.source_setting_option.tag.blended'
                            )}
                        </BlendedTag>
                    )}
                    {renderGssLink(account)}
                    {isQa && <ClickToCopyAssetId assetId={assetId} />}
                    <CloseIcon
                        class="close"
                        v-view-box
                        onClick={(e) => {
                            if (disabled) return;
                            e.stopPropagation();
                            updateSource(account);
                        }}
                    ></CloseIcon>
                </DataSourceItemBox>
            );
        },
        filterByCategory({ assetId, items }) {
            return R.any(R.propEq('assetId', assetId), items);
        },
        customTextGenerator(source) {
            const text = this.translator(source);

            if (mappingActivated(source)) {
                return (
                    <div>
                        <BlendedTagWrapper>
                            <BlendedText {...DIRECTIVE_CLAMP}>
                                {text}
                            </BlendedText>
                            <MappingTag>
                                {this.$t(
                                    'NEW_DASHBOARD.source_setting_option.tag.mapped'
                                )}
                            </MappingTag>
                        </BlendedTagWrapper>
                    </div>
                );
            }

            if (blendingActivated(source)) {
                return (
                    <div>
                        <BlendedTagWrapper>
                            <BlendedText {...DIRECTIVE_CLAMP}>
                                {text}
                            </BlendedText>
                            <BlendedTag>
                                {this.$t(
                                    'NEW_DASHBOARD.source_setting_option.tag.blended'
                                )}
                            </BlendedTag>
                        </BlendedTagWrapper>
                    </div>
                );
            }

            return <div>{text}</div>;
        },
        renderSelect() {
            const {
                isAnalytic,
                localeKey,
                searchText,
                currentList,
                updateSource,
                filterByCategory,
                makeTooltip,
                translator,
                indexKey,
                rawList,
                setSearchText,
                lazySeachText,
                adAccountsMapByChannel,
                showAddConnection,
                sourceCategoryNameGenerator,
                checkAllDisabled,
                checkItemDisabled,
                isLimittedConnector,
                customTextGenerator,
                hasOnlyAvailableConnectors,
            } = this;

            const SOURCE_LOOP_TARGET = Object.entries(adAccountsMapByChannel);

            return (
                <C.ContentWrapper class="campaign-header-content-wrapper">
                    <div class="dummy" />
                    <C.ContentGuideWrapper>
                        <span class="main-guide">
                            {this.$t(
                                `NEW_DASHBOARD.second_panel_main_guide_${localeKey}`
                            )}
                        </span>
                        {!isAnalytic && (
                            <span class="sub-guide">
                                {this.$t(
                                    `NEW_DASHBOARD.second_panel_sub_guide_${localeKey}`
                                )}
                            </span>
                        )}
                    </C.ContentGuideWrapper>
                    <C.DropdownSearchLabel>
                        <SearchInput
                            value={searchText}
                            onInput={setSearchText}
                            iconAlign="right"
                            placeholder={this.$t('NEW_DASHBOARD.search')}
                        />
                        {showAddConnection && (
                            <PermissionTooltip
                                visible={!this.connectionManageable}
                            >
                                <C.BreakdownBtn
                                    disabled={!this.connectionManageable}
                                    onClick={() => {
                                        mixpanelLogger.logEvent(
                                            'widget_setting_add_datasource_clicked'
                                        );
                                        this.connectionPanel = true;
                                    }}
                                    lang={this.language}
                                >
                                    <PlusIcon v-view-box />
                                    {this.$t(
                                        'NEW_DASHBOARD.second_panel_title_add_source'
                                    )}
                                </C.BreakdownBtn>
                            </PermissionTooltip>
                        )}
                    </C.DropdownSearchLabel>
                    <SecondPanel
                        loopTarget={SOURCE_LOOP_TARGET}
                        currentList={currentList}
                        hasCheckbox={true}
                        hasCheckboxAll={!hasOnlyAvailableConnectors}
                        onClickCheckBox={updateSource}
                        onUpdate={(v) => {
                            this.$emit('update', v);
                        }}
                        getCategory={refineChannel}
                        filterByCategory={filterByCategory}
                        tooltipGenerator={makeTooltip}
                        isItemDisabled={checkItemDisabled}
                        isCheckAllDisabled={checkAllDisabled}
                        hideCategoryCheckbox={this.isTemplateView}
                        disabledOnClick={(v) => {
                            if (isLimittedConnector(v)) {
                                this.renderWarningToLimiedUser();
                            }
                        }}
                        translator={translator}
                        indexKey={indexKey}
                        searchText={lazySeachText}
                        rawList={rawList}
                        additionalIconGenerator={this.renderSourceItemIcons}
                        categoryNameGenerator={sourceCategoryNameGenerator}
                        customTextGenerator={customTextGenerator}
                        // to update ractiveList(blended tag)
                        rListUpdateKey={UPDATE_KEY}
                        rListCompareFn={compareFn}
                        unselectAllLabelKey="COMMON.reset"
                    />
                </C.ContentWrapper>
            );
        },
        checkAllDisabled(category, list = []) {
            /**
             * In the template view, only one datasource can be selected per channel,
             * so category selection is disabled.
             */
            if (this.isTemplateView) return true;

            if (this.ignoreIsAnalytic) return false;

            if (
                this.widgetType === PIVOT_TABLE ||
                this.widgetType === PIVOT_TABLE_ANALYTIC
            ) {
                return false;
            }

            /** 데이터스트림, 자동화일 때만 기존 로직 유지 */
            if (
                this.widgetType === DATA_STERAM ||
                this.widgetType === AUTOMATION
            ) {
                const firstItem = R.head(list);
                const isAnalyticSource = checkIsAnalyticSource(firstItem);

                const isExternalPlatform = R.compose(
                    R.propSatisfies(
                        R.includes(R.__, DISPLAY_CHANNEL_NAME_CHANNELS),
                        'channel'
                    ),
                    R.defaultTo({})
                )(firstItem);

                if (
                    [
                        ...ALL_CHECK_DISABLE_CATEGORIES,
                        'googleAnalytics',
                        'googleAnalytics4',
                    ].includes(category) ||
                    isExternalPlatform ||
                    (isAnalyticSource && !firstItem.isSupportMultiDatasources)
                ) {
                    return true;
                }
            }

            if (category === 'all') {
                const hasOption = R.any(
                    R.compose(isValueExist, this.generateOptions),
                    this.currentList
                );
                const notSelected = !isValueExist(this.selectedList);

                if (notSelected) {
                    /**
                     * Check if all sources are compatible within marketing mode
                     * - Native Marketing Source + Analytic Source(Mapped + Blended)
                     */
                    const isAllMarketingLike = R.all(isMarketingLikeSource)(
                        this.currentList
                    );

                    /**
                     * Check if all sources are compatible within analytic mode
                     * - Analytic Source Exclude Mapped Source
                     */
                    const isAllAnalytic = R.all(
                        R.complement(isMarketingSource)
                    )(this.currentList);

                    return !isAllMarketingLike && !isAllAnalytic;
                }

                return hasOption;
            }

            return R.all(R.compose(isValueExist, this.generateOptions), list);
        },
        checkItemDisabled(v) {
            const { makeTooltip, isLimittedConnector } = this;

            return Boolean(makeTooltip(v) || isLimittedConnector(v));
        },
        sourceCategoryNameGenerator({ category = '', list = [] }) {
            const { language, translateOr: tOr } = this;
            const firstItem = R.head(list);

            const displayName =
                language === 'ko'
                    ? firstItem.displayChannelNameKR ||
                      firstItem.displayChannelName
                    : firstItem.displayChannelName;

            const basicCategoryTranslate = getChannelName.call(
                this,
                category,
                tOr(
                    `NEW_DASHBOARD.category_${category}`,
                    refineRawText(category)
                )
            );

            let refinedCategoryName = displayName || basicCategoryTranslate;

            if (firstItem.channel === 'airbyte') {
                const {
                    dataSourceName = '',
                    displayChannelNameKR,
                    displayChannelName,
                } = firstItem;

                refinedCategoryName =
                    language === 'ko'
                        ? displayChannelNameKR || displayChannelName
                        : displayChannelName;

                if (dataSourceName) {
                    refinedCategoryName =
                        refinedCategoryName + '-' + dataSourceName;
                }
            }

            return refinedCategoryName;
        },
        adsGenerateOption(item) {
            const { widgetType } = this;

            const isDataStream = widgetType === DATA_STERAM;

            if (!isDataStream) return { options: [], message: '' };

            const isMarketingLike = isMarketingLikeSource(item);

            if (isMarketingLike) {
                return { options: [], message: '' };
            }

            return {
                options: [UNSELECT_ALREADY_SELECTED],
                message: this.$t(
                    'EXPORT_DATA.not_allowed_source_without_blend_desc'
                ),
            };
        },
        makeTooltip(item) {
            if (item.status && item.status !== 'ACTIVE') {
                return this.$t(`CONNECTION.dashboard_status_${item.status}`);
            }

            if (
                this.widgetType !== DATA_STERAM &&
                !item.selected &&
                item.isDeprecated
            ) {
                return this.$t(
                    'NEW_DASHBOARD.source_setting_option.tooltip.deprecated_source'
                );
            }

            const { disabledTooltipGenerator } = this;

            const disabledTooltip =
                disabledTooltipGenerator && disabledTooltipGenerator(item);

            if (disabledTooltip) {
                return disabledTooltip;
            }

            if (this.ignoreIsAnalytic) return '';

            if (this.isTemplateView) {
                // A just-toggled item can render as unselected for a frame while
                // selectedList (which drives maxedOutChannelSet) already counts it,
                // briefly blocking the item that maxed its own channel.
                const isSelected =
                    item.selected ||
                    this.selectedList.some(
                        (s) => getAccountKey(s) === getAccountKey(item)
                    );
                if (isSelected) {
                    return '';
                }

                if (item.isFreeFormat) {
                    return this.$t(
                        'NEW_DASHBOARD.new_template.source_setting_panel.free_format_datasource_tooltip'
                    );
                }

                const channel = refineChannel(item);
                const isChannelMaxedOut = this.maxedOutChannelSet.has(channel);
                if (isChannelMaxedOut) {
                    return this.$t(
                        'NEW_DASHBOARD.new_template.source_setting_panel.channel_maxed_out_tooltip',
                        { limit: MAX_TEMPLATE_AD_ACCOUNTS_PER_CHANNEL }
                    );
                }
            }

            const isEmpty = !isValueExist(this.selectedList);
            const isMapped = mappingActivated(item);
            const isBlended = blendingActivated(item);
            const isNotMappedYet = isMappableSource(item) && !isMapped;
            const isNotBlendedYet = isBlendableSource(item) && !isBlended;

            switch (this.widgetType) {
                /** creative widget */
                case CREATIVE: {
                    const isMarketing = isMarketingSource(item);
                    const isOrganic = R.propEq(
                        'dataSourcePrefix',
                        'organic',
                        item
                    );

                    if (isMarketing) return '';

                    if (this.isAnalytic) {
                        if (isOrganic) return '';

                        if (isBlended)
                            return this.$t(
                                'NEW_DASHBOARD.source_setting_option.tooltip.blended_source_in_generic_creative_widget'
                            );
                    } else {
                        if (isBlended) return '';

                        if (isOrganic)
                            return this.$t(
                                'NEW_DASHBOARD.source_setting_option.tooltip.blendable_source_in_marketing_widget'
                            );

                        if (isNotMappedYet) {
                            return this.$t(
                                'NEW_DASHBOARD.source_setting_option.tooltip.mappable_source_in_marketing_table_widget'
                            );
                        }

                        if (isNotBlendedYet)
                            return this.$t(
                                'NEW_DASHBOARD.source_setting_option.tooltip.blendable_source_in_marketing_table_widget'
                            );
                    }

                    return this.$t(
                        'NEW_DASHBOARD.source_setting_option.tooltip.unsupported_source_in_creative_widget'
                    );
                }
                /** widget has blendToggleOption */
                case SINGLE_METRIC:
                case OVERVIEW:
                case GOAL:
                case GRAPH:
                case SMALL_TABLE: {
                    // 첫번째 데이터 소스 선택에 따라 widget mode를 결정하기 위함. 비어져 있는 상태에서는 모든 데이터소스 선택 가능
                    if (isEmpty) return '';

                    if (!this.isAnalytic) {
                        if (isBlended || isMapped) return '';

                        if (isNotMappedYet) {
                            return this.$t(
                                'NEW_DASHBOARD.source_setting_option.tooltip.mappable_source_in_marketing_widget'
                            );
                        }
                        if (isNotBlendedYet) {
                            return this.$t(
                                'NEW_DASHBOARD.source_setting_option.tooltip.blendable_source_in_marketing_widget'
                            );
                        }
                    }

                    return '';
                }
                /**
                 * marketing table widget
                 */
                case PIVOT_TABLE: {
                    if (isBlended || isMapped) return '';

                    if (isNotMappedYet) {
                        return this.$t(
                            'NEW_DASHBOARD.source_setting_option.tooltip.mappable_source_in_marketing_table_widget'
                        );
                    }

                    if (isNotBlendedYet) {
                        return this.$t(
                            'NEW_DASHBOARD.source_setting_option.tooltip.blendable_source_in_marketing_table_widget'
                        );
                    }

                    return '';
                }
            }

            return '';
        },
        isLimittedConnector(item) {
            const { hasOnlyAvailableConnectors, pricePlanSettings } = this;

            const { availableConnectorTypes = [], allowAllConnectorTypes } =
                pricePlanSettings;

            if (hasOnlyAvailableConnectors && !allowAllConnectorTypes) {
                const { channel = '' } = item;

                const isAvailableConnector = availableConnectorTypes.some(
                    (c = '') =>
                        R.startsWith('internal_', c)
                            ? R.replace('internal_', '', c) === channel ||
                              c === channel
                            : c === channel
                );

                return !isAvailableConnector;
            } else {
                return false;
            }
        },
        renderConnectionPanel() {
            const {
                connectionPanel,
                getTableAdAccounts,
                accountToken,
                isAnalytic,
            } = this;

            if (!connectionPanel) return;

            const panelProps = {
                attrs: {
                    active: connectionPanel,
                    token: accountToken,
                    isAnalytic,
                },
                on: {
                    close: () => (this.connectionPanel = false),
                    refresh: () => {
                        getTableAdAccounts();

                        if (this.isReactDashboard) {
                            this.postIframeMessage({
                                type: IFRAME_EVENT_MESSAGE.REFRESH_AD_ACCOUNTS,
                            });
                        }
                    },
                },
            };
            return <ConnectionPanel {...panelProps} />;
        },
        renderAskingTableModeChange() {
            if (!this.askingModal) return;

            const props = {
                attrs: {
                    active: this.askingModal,
                    backdrop: true,
                    hasCancelButton: true,
                    hasConfirmButton: false,
                },
                on: {
                    'update:active': (v) => (this.askingModal = v),
                    close: this.closeAskingModal,
                    cancel: this.closeAskingModal,
                },
            };

            return (
                <portal to="panel_modal">
                    <Dialog {...props}>
                        <AskingModalWrapper>
                            <AskingModalTitle>
                                {this.$t(
                                    'NEW_DASHBOARD.change_source_mode_modal_title'
                                )}
                            </AskingModalTitle>
                            <AskingModalDesc
                                domPropsInnerHTML={this.generateAskingTableModeChangeDesc(
                                    this.selectedData
                                )}
                            ></AskingModalDesc>
                            {this.renderOptions(this.selectedData)}
                        </AskingModalWrapper>
                    </Dialog>
                </portal>
            );
        },
        generateAskingTableModeChangeDesc(data) {
            const fnByType = this.isAnalytic
                ? this.analyticGenerateOption
                : this.adsGenerateOption;

            const { message = '' } = fnByType(data);

            return message;
        },
        generateOptions(data) {
            if (this.ignoreIsAnalytic) return [];

            if (
                !isValueExist(this.selectedList) ||
                R.any(R.propEq('channelId', data.channelId), this.selectedList)
            ) {
                return [];
            }

            const fnByType = this.isAnalytic
                ? this.analyticGenerateOption
                : this.adsGenerateOption;

            const { options = [] } = fnByType(data);

            return options;
        },
        renderOptions(data) {
            const options = this.generateOptions(data);

            return (
                <AskingModalOptionWrapper>
                    {options.map((v) => {
                        const fn = this.clickEventByType(v);
                        return (
                            <AskingModalOption onClick={() => fn(data)}>
                                {this.displayCtaName(v)}
                            </AskingModalOption>
                        );
                    })}
                </AskingModalOptionWrapper>
            );
        },
        displayCtaName(type) {
            switch (type) {
                case UNSELECT_ALREADY_SELECTED:
                    return this.$t(
                        'NEW_DASHBOARD.change_source_mode_modal_options_select_source'
                    );
            }
        },
        clickChangeMode({ channelId }) {
            this.$emit(
                'update',
                R.map(
                    (v) => R.assoc('selected', channelId === v.channelId, v),
                    this.currentList
                )
            );
            this.$emit('resetTableValues', true);
            this.closeAskingModal();
        },
        clickEventByType(type) {
            switch (type) {
                case UNSELECT_ALREADY_SELECTED:
                    return this.clickChangeMode;
            }
        },
        closeAskingModal() {
            this.selectedData = {};
            this.askingModal = false;
        },
        renderDisconnectedSourceInfo() {
            const { availableList } = this;
            const hasDisconnectedSource = R.any(
                R.allPass([
                    (source) =>
                        !isValueExist(
                            this.tableAdAccountsByAccountKey[
                                getAccountKey(source)
                            ]
                        ),
                    R.prop('selected'),
                ]),
                availableList
            );

            if (!hasDisconnectedSource) return;

            return (
                <DisconnectedInfoWrapper>
                    <WarningIcon class="warning-icon" />
                    <AdditionalInfoText>
                        {this.$t(
                            'NEW_DASHBOARD.source_setting_option.warning.disconnected_source'
                        )}
                    </AdditionalInfoText>

                    <AdditionalInfoCtaSlot>
                        <PermissionTooltip visible={!this.connectionManageable}>
                            <AdditionalInfoCta
                                disabled={!this.connectionManageable}
                                onClick={() => {
                                    const routerData = this.$router.resolve({
                                        name: 'Connection',
                                        query: {
                                            userAccountId: this.cAccountId,
                                        },
                                    });
                                    window.open(routerData.href, '_blank');
                                }}
                            >
                                <DisconnectedInfoCtaText>
                                    {this.$t(
                                        'NEW_DASHBOARD.source_setting_option.warning.disconnected_source_cta'
                                    )}
                                </DisconnectedInfoCtaText>
                                <NextIcon class="next-icon" v-view-box />
                            </AdditionalInfoCta>
                        </PermissionTooltip>
                    </AdditionalInfoCtaSlot>
                </DisconnectedInfoWrapper>
            );
        },
        renderInvalidSourceInfo() {
            const { availableList, isAnalytic } = this;
            const hasInvalidSource = R.any(
                R.allPass([
                    (source) => {
                        const latestAccount =
                            this.tableAdAccountsByAccountKey[
                                getAccountKey(source)
                            ];

                        return isAnalytic
                            ? isMarketingSource(latestAccount)
                            : !isMarketingLikeSource(latestAccount);
                    },
                    R.prop('selected'),
                ]),
                availableList
            );
            if (!hasInvalidSource) return;

            return (
                <InvalidInfoWrapper>
                    <WarningIcon class="warning-icon" />
                    <AdditionalInfoText>
                        {this.$t(
                            'NEW_DASHBOARD.source_setting_option.warning.invalid_source'
                        )}
                    </AdditionalInfoText>

                    <AdditionalInfoCtaSlot>
                        <PermissionTooltip visible={!this.connectionManageable}>
                            <AdditionalInfoCta
                                disabled={!this.connectionManageable}
                                onClick={() => {
                                    const routerData = this.$router.resolve({
                                        name: 'Connection',
                                        query: {
                                            userAccountId: this.cAccountId,
                                        },
                                    });
                                    window.open(routerData.href, '_blank');
                                }}
                            >
                                <InvalidInfoCtaText>
                                    {this.$t(
                                        'NEW_DASHBOARD.source_setting_option.warning.invalid_source_cta'
                                    )}
                                </InvalidInfoCtaText>
                                <NextIcon class="next-icon" v-view-box />
                            </AdditionalInfoCta>
                        </PermissionTooltip>
                    </AdditionalInfoCtaSlot>
                </InvalidInfoWrapper>
            );
        },
        renderRedirectConfirmModal() {
            const { redirectTargetItem } = this;

            if (!isValueExist(redirectTargetItem)) return;

            const props = {
                attrs: {
                    active: isValueExist(redirectTargetItem),
                    backdrop: true,
                    emitCancelOnClickOutside: false,
                    title: this.$t(
                        'NEW_DASHBOARD.source_setting_option.dialog.redirect_to_settings_title'
                    ),
                    confirmText: this.$t(
                        'NEW_DASHBOARD.source_setting_option.dialog.redirect_to_settings_confirm'
                    ),
                },
                on: {
                    confirm: () => {
                        const assetId = redirectTargetItem.assetId;
                        const settingsTab = this.isAnalytic
                            ? 'general'
                            : 'data-integration';

                        this.$router.push({
                            name: 'Connection',
                            query: {
                                userAccountId: this.cAccountId,
                                assetId,
                                settingsTab,
                            },
                        });

                        this.redirectTargetItem = null;
                    },
                    cancel: () => (this.redirectTargetItem = null),
                    'update:active': () => (this.redirectTargetItem = null),
                },
            };

            return (
                <portal to="panel_modal">
                    <Dialog {...props} />
                </portal>
            );
        },
    },
    render() {
        const {
            renderSelect,
            renderList,
            wrapOptions,
            renderSecondPanel,
            localeKey,
            renderConnectionPanel,
            hasEditBtn,
            renderAskingTableModeChange,
            renderDisconnectedSourceInfo,
            renderInvalidSourceInfo,
            renderRedirectConfirmModal,
        } = this;

        return (
            <C.ComponentWrapper hide={this.hide}>
                {renderAskingTableModeChange()}
                {renderRedirectConfirmModal()}
                {wrapOptions({
                    children: renderList(),
                    title: this.$t('NEW_DASHBOARD.filter_type_source'),
                    icon: <DataSourceIcon v-view-box class="metric-icon" />,
                    hasEditBtn,
                })}
                {renderSecondPanel({
                    title: this.$t(
                        `NEW_DASHBOARD.second_panel_title_${localeKey}`
                    ),
                    content: renderSelect(),
                })}
                {renderConnectionPanel()}
                {renderDisconnectedSourceInfo()}
                {renderInvalidSourceInfo()}
            </C.ComponentWrapper>
        );
    },
};

export default withHMR(
    module,
    PanelDataSourceOption,
    PanelDataSourceOption.name
);
